import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, collection, getDocs, limit, query } from 'firebase/firestore';

// Firebase 구성 정보
const firebaseConfig = {
  apiKey: "AIzaSyBZtupWsi2TauHkFkPJlXQjAvd30SkbIQA",
  authDomain: "whatquizaiplatform.firebaseapp.com",
  projectId: "whatquizaiplatform",
  storageBucket: "whatquizaiplatform.firebasestorage.app",
  messagingSenderId: "590620435886",
  appId: "1:590620435886:web:28cf4e39332d0c5539543f",
  measurementId: "G-TNPQ7YLR51"
};

// Firebase 앱 초기화 (동기적으로 필요한 최소한의 부분만)
let app = null;
let auth = null;
let db = null;
let teacheringDb = null; // teachering-class 데이터베이스

try {
  app = initializeApp(firebaseConfig);
  if (app) {
    auth = getAuth(app);
    db = getFirestore(app); // (default) 데이터베이스
    teacheringDb = getFirestore(app, 'teachering-class'); // teachering-class 데이터베이스
    
    // 🧪 Emulator 연결 (REACT_APP_USE_EMULATOR=true일 때만)
    if (process.env.REACT_APP_USE_EMULATOR === 'true') {
      console.log('🧪 Firebase Emulator에 연결 중...');
      
      // Auth Emulator 연결
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      
      // Firestore Emulator 연결 - (default) DB
      connectFirestoreEmulator(db, 'localhost', 8080);
      
      // Firestore Emulator 연결 - teachering-class DB
      connectFirestoreEmulator(teacheringDb, 'localhost', 8080);
      
      console.log('✅ Emulator 연결 완료!');
      console.log('  - Auth: http://localhost:9099');
      console.log('  - Firestore (default): localhost:8080');
      console.log('  - Firestore (teachering-class): localhost:8080');
      console.log('');
      console.log('🔍 각 데이터베이스 규칙 테스트 중...');
      console.log('   → Emulator UI (http://localhost:4000 → Logs 탭)에서 규칙 평가 로그를 확인하세요!');
      
      // 규칙 테스트 실행 (비동기)
      setTimeout(() => testEmulatorRules(), 1000);
    }
    
    // rememberMe 여부에 따라 초기 지속성 결정
    try {
      const remembered = typeof window !== 'undefined' && window.localStorage?.getItem('rememberMe') === 'true';
      setPersistence(auth, remembered ? browserLocalPersistence : browserSessionPersistence).catch(() => {});
    } catch (_) {}
  }
} catch (error) {
  console.error('Firebase 핵심 초기화 오류:', error);
}

export { app, auth, db, teacheringDb };

// 현재 사용자 가져오기 (가장 자주 사용되는 함수)
export const getCurrentUser = () => {
  return auth?.currentUser || null;
};

// 익명 로그인 (초기 화면에서 바로 필요)
export const signInAnonymousUser = async () => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    const { signInAnonymously } = await import('firebase/auth');
    const userCredential = await signInAnonymously(auth);
    
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Google 리다이렉트 결과 처리 (페이지 로드 시 바로 필요)
export const handleGoogleRedirectResult = async () => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다.", isRedirectResult: false };
    }
    
    const { getRedirectResult } = await import('firebase/auth');
    const result = await getRedirectResult(auth);
    
    if (result && result.user) {
      return { 
        user: result.user, 
        error: null, 
        isRedirectResult: true,
        credential: result.credential 
      };
    } else {
      return { user: null, error: null, isRedirectResult: false };
    }
  } catch (error) {
    return { user: null, error: error.message, isRedirectResult: true };
  }
};

// 🧪 Emulator 적용된 규칙 정보 출력
const testEmulatorRules = async () => {
  if (process.env.REACT_APP_USE_EMULATOR !== 'true') return;
  
  console.log('');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('📋 Firebase Emulator 적용된 보안 규칙');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('');
  
  // ===== Database 1: (default) =====
  console.log('🔵 데이터베이스: (default)');
  console.log('📄 규칙 파일: firestore.rules');
  console.log('📍 파일 위치: ./firestore.rules');
  console.log('');
  console.log('주요 규칙:');
  console.log('  ┌─ debate_group/{roomCode}');
  console.log('  │  └─ allow read: if true  (비로그인 허용)');
  console.log('  │');
  console.log('  ┌─ groups/{groupId}');
  console.log('  │  └─ allow read: if true  (비로그인 허용)');
  console.log('  │');
  console.log('  ┌─ mind_map_sessions/{sessionId}');
  console.log('  │  └─ allow read, write: if true  (비로그인 허용)');
  console.log('  │');
  console.log('  ┌─ users/{userId}');
  console.log('  │  └─ allow read, write: if request.auth != null && request.auth.uid == userId');
  console.log('  │');
  console.log('  └─ /{document=**}');
  console.log('     └─ allow read, write: if request.auth != null  (기본 인증 필수)');
  console.log('');
  console.log('─────────────────────────────────────────────────────────────');
  console.log('');
  
  // ===== Database 2: teachering-class =====
  console.log('🟣 데이터베이스: teachering-class');
  console.log('📄 규칙 파일: firestore-teachering.rules');
  console.log('📍 파일 위치: ./firestore-teachering.rules');
  console.log('');
  console.log('주요 규칙 (🔒 엄격한 권한 관리):');
  console.log('  ┌─ classes/{classId}');
  console.log('  │  └─ allow read: if request.auth.uid == resource.data.teacherId');
  console.log('  │     (담임교사만 접근 가능 - 관리자 제외됨)');
  console.log('  │');
  console.log('  ┌─ classes/{classId}/students/{studentId}');
  console.log('  │  └─ allow read: if 담임교사 || 본인');
  console.log('  │  └─ allow write: if 담임교사만');
  console.log('  │');
  console.log('  ┌─ students/{studentId}');
  console.log('  │  └─ allow read, write: if request.auth.uid == studentId');
  console.log('  │     (학생 본인만 접근 가능 - 관리자 제외됨)');
  console.log('  │');
  console.log('  ┌─ school-record/{teacherId}');
  console.log('  │  └─ allow read, write: if request.auth.uid == teacherId');
  console.log('  │     (교사 본인만 접근 가능 - 관리자 제외됨)');
  console.log('  │');
  console.log('  └─ /{document=**}');
  console.log('     └─ allow read: if request.auth != null');
  console.log('     └─ allow write: if false  (명시적 차단)');
  console.log('');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('');
  console.log('📊 규칙 확인 방법:');
  console.log('  1. Emulator UI: http://localhost:4000');
  console.log('  2. Firestore 탭 → Requests 탭');
  console.log('  3. 각 요청 클릭 → 규칙 평가 과정 확인');
  console.log('');
  console.log('📝 규칙 파일 전체 보기:');
  console.log('  • firestore.rules 보기:');
  console.log('    http://localhost:8080/emulator/v1/projects/whatquizaiplatform:ruleCoverage.html');
  console.log('');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('');
  
  // 실제 요청으로 Emulator UI Logs에 표시
  try {
    console.log('🔍 규칙 동작 테스트 중... (Emulator UI Logs 탭 확인)');
    
    // Default DB 테스트
    try {
      const debateGroupRef = collection(db, 'debate_group');
      const q = query(debateGroupRef, limit(1));
      await getDocs(q);
      console.log('  ✅ Default DB: debate_group 읽기 성공 (비로그인 허용)');
    } catch (error) {
      console.log('  ℹ️  Default DB:', error.code);
    }
    
    // teachering-class DB 테스트
    try {
      const classesRef = collection(teacheringDb, 'classes');
      const q = query(classesRef, limit(1));
      await getDocs(q);
      console.log('  ✅ teachering-class DB: classes 읽기 성공');
    } catch (error) {
      if (error.code === 'permission-denied') {
        console.log('  ✅ teachering-class DB: classes 접근 거부됨 (규칙 작동 중)');
      }
    }
    
    console.log('');
    console.log('✅ 규칙 테스트 완료! Emulator UI Logs에서 상세 내용을 확인하세요.');
    
  } catch (error) {
    console.error('❌ 규칙 테스트 중 오류:', error);
  }
}; 